/** Docs (UA): https://prostopay.atlassian.net/wiki/spaces/SW/pages/1902149638 — Точка входу — ScanQrCode */

import {
    DEV_MACHINES,
    IS_DEV,
    IS_STAGE,
    QR_SERVICES,
} from '../../shared/constants';
import { VendingMachine } from '../../shared/types';
import { debug, getVmDetails } from '../../shared/lib/general';
import {
    checkQrData,
    checkQrServiceData,
} from '../../modules/order/api/order.api';
import {
    clearAllSavedStates,
    loadSavedVMState,
} from '../../shared/lib/stateStorage';

export interface CheckQrProps {
    qr: string;
    onSuccess: (vm: VendingMachine) => void;
    onFailure: () => void;
    onNotSupported: (qr: string) => void;
    onNotConnected: (qr: string) => void;
    onFiscalService: (qr: string) => void;
    onMainService: (qr: string) => void;
    onsetVm: (vm: VendingMachine) => void;
    onFeedbacks: (qr: string, vm?: number) => void;
}

interface CheckQrOptions {
    /** When true, uses the service-flow endpoint and routing. */
    service?: boolean;
}

/**
 * Validates a scanned QR code and routes the user to the correct screen
 * (payment, service menu, fiscal receipt, feedback, or error page).
 *
 * When `service: true`, the service-flow variant is used (triggered by
 * `?service=1` in the URL). It hits a different API endpoint and follows a
 * different routing table that favors service menus over direct purchase.
 */
export const checkQr = (
    params: CheckQrProps,
    options: CheckQrOptions = {},
): void => {
    if (options.service) {
        runServiceFlow(params);
    } else {
        runPaymentFlow(params);
    }
};

/**
 * Staging-only gate: when not on dev builds but on stage, only `DEV_MACHINES`
 * are allowed through. Returns true when the caller should stop.
 */
function isStagingBlocked(qr: string): boolean {
    if (!IS_DEV && IS_STAGE && !DEV_MACHINES.includes(qr)) {
        debug('Not supported VM', 'info');
        return true;
    }
    return false;
}

/**
 * Shared "can this VM accept a purchase?" gate used by both flows. Mirrors the
 * original `purchase.ts` / service-flow local `checkOrder` logic.
 */
function routeToOrder(vm: VendingMachine, qr: string, params: CheckQrProps): void {
    const { isAllowed, isFridge, isFridgeSmart, isCT5, isConnected } =
        getVmDetails(vm);

    if ((!isAllowed && !isFridge) || isFridgeSmart) {
        debug('Not supported VM', 'info');
        params.onNotSupported(qr);
    } else if (!isConnected && isCT5) {
        debug('Not connected AND CT5', 'info');
        params.onNotConnected(qr);
    } else {
        params.onSuccess(vm);
    }
}

// -----------------------------------------------------------------------------
// Payment flow (non-service QR scan)
// -----------------------------------------------------------------------------

function runPaymentFlow(params: CheckQrProps): void {
    const { qr, onsetVm, onNotSupported } = params;

    if (isStagingBlocked(qr)) {
        onNotSupported(qr);
        return;
    }

    const storedData = loadSavedVMState();
    debug('storedData 195', 'data', storedData);
    const { isSavedDataExpired } = storedData;
    const isSameQr = storedData?.qr === qr;

    if (storedData && !isSavedDataExpired && isSameQr) {
        debug('using stored data', 'data', storedData);
        onsetVm(storedData as unknown as VendingMachine);
        return;
    }

    debug('no stored data', 'error');
    void fetchAndRoutePaymentFlow(params);
}

async function fetchAndRoutePaymentFlow(params: CheckQrProps): Promise<void> {
    const { qr, onFailure, onsetVm, onNotSupported } = params;
    try {
        const result = await checkQrData(qr);
        const { QRservices, feedbacks, fisc_receipts } = result;
        onsetVm(result);
        debug('vmDetails', 'data', getVmDetails(result));

        switch (QRservices) {
            case QR_SERVICES.NOTHING_IS_ALLOWED:
                onNotSupported(qr);
                return;
            case QR_SERVICES.SEPARATED_SERVICES:
                routeAllowedOrFallback('notSupported', result, params);
                return;
            default:
                routeByFiscalStatus(
                    feedbacks === 1,
                    fisc_receipts === 1,
                    result,
                    params,
                );
                return;
        }
    } catch (error) {
        console.error(error);
        onFailure();
    }
}

function routeByFiscalStatus(
    isFeedbacksEnabled: boolean,
    hasFiscalReceipt: boolean,
    vm: VendingMachine,
    params: CheckQrProps,
): void {
    let action: PaymentFallbackAction = 'notSupported';

    if (isFeedbacksEnabled) {
        action = hasFiscalReceipt ? 'mainService' : 'feedbacks';
    } else if (hasFiscalReceipt) {
        action = 'fiscalService';
    }

    routeAllowedOrFallback(action, vm, params);
}

type PaymentFallbackAction =
    | 'notSupported'
    | 'fiscalService'
    | 'mainService'
    | 'feedbacks';

function routeAllowedOrFallback(
    action: PaymentFallbackAction,
    vm: VendingMachine,
    params: CheckQrProps,
): void {
    const { qr } = params;
    const { isAllowed, isFridge } = getVmDetails(vm);
    debug(
        `✅ Allowed: ${isAllowed} \nisFridge: ${isFridge}\naction: ${action} \nqr: ${qr}`,
        'error',
    );

    if (isAllowed || isFridge) {
        routeToOrder(vm, qr, params);
        return;
    }

    debug(`🚫 Not allowed VM: ${qr}, action: ${action}`, 'error');
    switch (action) {
        case 'notSupported':
            debug('🚫 notSupported', 'error');
            clearAllSavedStates();
            params.onNotSupported(qr);
            return;
        case 'fiscalService':
            debug('🚫 fiscalService', 'error');
            params.onFiscalService(qr);
            return;
        case 'mainService':
            debug('🚫 mainService', 'error');
            params.onMainService(qr);
            return;
        case 'feedbacks':
            debug('🚫 feedbacks', 'error');
            params.onFeedbacks(qr, vm.vm_id);
            return;
    }
}

// -----------------------------------------------------------------------------
// Service flow (entered via ?service=1 in the URL)
// -----------------------------------------------------------------------------

async function runServiceFlow(params: CheckQrProps): Promise<void> {
    const { qr, onFailure, onsetVm, onNotSupported } = params;
    try {
        const result = await checkQrServiceData(qr);
        const resultQr = result.qr;
        onsetVm(result);

        if (isStagingBlocked(resultQr)) {
            onNotSupported(resultQr);
            return;
        }

        routeServiceByQrServices(result, params);
    } catch (error) {
        console.error(error);
        onFailure();
    }
}

function routeServiceByQrServices(
    vm: VendingMachine,
    params: CheckQrProps,
): void {
    const { onNotSupported } = params;
    const resultQr = vm.qr;
    const { isAllowed, isFridge, isFeedbackEnabled, hasFiscalReceipt } =
        getVmDetails(vm);
    const isAllowedOrFridge = isAllowed || isFridge;

    switch (vm.QRservices) {
        case QR_SERVICES.SEPARATED_SERVICES:
            routeServiceSeparated(
                isFeedbackEnabled,
                hasFiscalReceipt,
                vm,
                params,
            );
            return;
        case QR_SERVICES.ALL_IS_PERMITTED:
            if (isFeedbackEnabled) {
                routeServiceAllPermittedWithFeedback(
                    hasFiscalReceipt,
                    isAllowedOrFridge,
                    vm,
                    params,
                );
            } else {
                routeServiceAllPermittedNoFeedback(
                    hasFiscalReceipt,
                    isAllowedOrFridge,
                    vm,
                    params,
                );
            }
            return;
        default:
            onNotSupported(resultQr);
    }
}

function routeServiceSeparated(
    isFeedbackEnabled: boolean | undefined,
    hasFiscalReceipt: boolean | undefined,
    vm: VendingMachine,
    params: CheckQrProps,
): void {
    const resultQr = vm.qr;
    if (isFeedbackEnabled && hasFiscalReceipt) return params.onMainService(resultQr);
    if (isFeedbackEnabled) return params.onFeedbacks(resultQr, vm.vm_id);
    if (hasFiscalReceipt) return params.onFiscalService(resultQr);
    params.onNotSupported(resultQr);
}

function routeServiceAllPermittedWithFeedback(
    hasFiscalReceipt: boolean | undefined,
    isAllowedOrFridge: boolean,
    vm: VendingMachine,
    params: CheckQrProps,
): void {
    const resultQr = vm.qr;
    if (hasFiscalReceipt || isAllowedOrFridge) return params.onMainService(resultQr);
    params.onFeedbacks(resultQr, vm.vm_id);
}

function routeServiceAllPermittedNoFeedback(
    hasFiscalReceipt: boolean | undefined,
    isAllowedOrFridge: boolean,
    vm: VendingMachine,
    params: CheckQrProps,
): void {
    const resultQr = vm.qr;
    if (hasFiscalReceipt) {
        isAllowedOrFridge
            ? params.onMainService(resultQr)
            : params.onFiscalService(resultQr);
        return;
    }
    if (isAllowedOrFridge) {
        routeToOrder(vm, resultQr, params);
    } else {
        params.onNotSupported(resultQr);
    }
}
